import { z } from 'zod';
import { RowLabelOptionalSchema } from './row-label';

export const SprayTaskSchema = z.object({
  id: z.number().int().positive(),
  company_id: z.string().uuid(),
  year: z.string().max(4).nullable().optional(),
  block_name: z.string().max(300).nullable().optional(),
  block_type: z.string().max(80).nullable().optional(),
  // spray_template.reference is a denormalized summary of EVERY block entry's
  // reference (the per-bed rows live in spray_plantings), so a multi-block spray
  // easily runs past 100 chars. The column is varchar(400) and the client already
  // builds the summary to that cap — this validator was the only thing still on
  // the old 100, rejecting the save outright ("Reference: String must contain at
  // most 100 Character(s)"). Keep it equal to the column width.
  reference: z.string().max(400).nullable().optional(),
  crop: z.string().max(120).nullable().optional(),
  crop_variety: z.string().max(150).nullable().optional(),
  start_time: z.string().nullable().optional(),
  finish_time: z.string().nullable().optional(),
  start_row: RowLabelOptionalSchema,
  finish_row: RowLabelOptionalSchema,
  chemical: z.string().max(255).nullable().optional(),
  chemical_ids: z.string().nullable().optional(),
  chemical_rate: z.number().nullable().optional(),
  water_rate: z.number().nullable().optional(),
  tank_size: z.string().max(100).nullable().optional(),
  spray_pressure: z.string().max(100).nullable().optional(),
  spray_jet_size: z.string().max(150).nullable().optional(),
  ppe_used: z.string().nullable().optional(),
  fertilizer: z.string().max(255).nullable().optional(),
  fertilizer_ids: z.string().nullable().optional(),
  fertilizer_rate: z.string().max(150).nullable().optional(),
  assigned_user: z.string().nullable().optional(),
  labour_id: z.string().nullable().optional(),
  total_hectares: z.number().nullable().optional(),
  rate_per_hectare: z.number().nullable().optional(),
  cost_per_unit: z.number().nullable().optional(),
  unit: z.string().max(100).nullable().optional(),
  task_status: z.string().max(100).nullable().optional(),
  // batch_number holds the task's whole batch allocation, not one batch number.
  // Three shapes exist in the wild (see apps/web/src/lib/batch-allocations.ts):
  // a plain comma list, the migrated `[{id,batch_no}]`, and the current
  // `[{id,type,batch_no,qty}]` which carries ONE ENTRY PER BATCH so a product can
  // be split across several. A 3-product task split two ways already runs ~400
  // chars. The column is TEXT (widened by
  // Archive/20260449_add_missing_spray_template_columns.sql) and live rows already
  // hold 217 chars — this validator was the last thing still on the old varchar(100)
  // and would reject the save outright. Keep it well clear of the column, like
  // `reference` above.
  batch_number: z.string().max(4000).nullable().optional(),
  tempreature: z.string().max(100).nullable().optional(),
  humidity: z.string().max(100).nullable().optional(),
  windspeed: z.string().max(100).nullable().optional(),
  direction: z.string().max(100).nullable().optional(),
  pest_observation_id: z.number().int().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  created_by: z.string().nullable().optional(),
  updated_by: z.string().nullable().optional(),
});

export const SprayTaskCreateSchema = SprayTaskSchema.omit({
  id: true,
  company_id: true,
  created_at: true,
  updated_at: true,
}).extend({
  is_manual_entry: z.number().int().nullable().optional(),
  block_id: z.number().int().nullable().optional(),
  week: z.number().int().nullable().optional(),
  percentage_area: z.number().nullable().optional(),
  rate: z.number().nullable().optional(),
  spray_color: z.string().max(100).nullable().optional(),
  saved_chemical: z.string().max(255).nullable().optional(),
  total_cost_products: z.number().nullable().optional(),
  categories: z.string().max(255).nullable().optional(),
  wind_speed_saved: z.string().max(100).nullable().optional(),
  is_type: z.string().max(100).nullable().optional(),
  is_custom_or_saved: z.string().max(100).nullable().optional(),
});

export const SprayTaskUpdateSchema = SprayTaskCreateSchema.partial();

export const SprayTaskListQuerySchema = z.object({
  search: z.string().optional(),
  block_type: z.string().optional(),
  page: z.coerce.number().int().positive().optional().default(1),
  per_page: z.coerce.number().int().positive().max(100).optional().default(25),
});

export type SprayTask = z.infer<typeof SprayTaskSchema>;
export type SprayTaskCreate = z.infer<typeof SprayTaskCreateSchema>;
export type SprayTaskUpdate = z.infer<typeof SprayTaskUpdateSchema>;
export type SprayTaskListQuery = z.infer<typeof SprayTaskListQuerySchema>;
